import { DateTime } from 'luxon';
import React, { useCallback, type JSX } from 'react';

import { accountsSelectors } from 'app-components/flux/accounts';
import { chartOfAccountsSelectors, chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import type {
  ChartOfAccountNodeProps,
  ChartOfAccountProps,
} from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';
import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { transactionsUtils } from 'app-components/flux/transactions';
import type { OrderedMap } from 'immutable';
import { useAppSelector } from 'hooks/redux';
import { makeStyles } from 'tss-react/mui';

import CallSplitIcon from '@mui/icons-material/CallSplit';
import InfoIcon from '@mui/icons-material/Info';
import LinkIcon from '@mui/icons-material/Link';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';

import QAmountField from 'components/QAmountField';
import { OverflowTooltip } from 'components/OverflowTooltip';
import QTip from 'components/QuickenControls/QTip';
import { TxnActionsMenuButton } from 'components/TxnActionHelpers/TxnActionsMenu';

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: `minmax(0, 1.5fr) minmax(0, 1.1fr) ${theme.spacing(14)} ${theme.spacing(5)}`,
    alignItems: 'center',
    columnGap: theme.spacing(2),
    padding: '16px',
    borderBottom: `1px solid ${theme.palette.border.light}`,
    '&:hover': {
      background: theme.palette.action.hover,
      cursor: 'pointer',
    },
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexGrow: 2,
    overflow: 'hidden',
    minWidth: 0,
  },
  payeeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    width: '100%',
  },
  payeeText: {
    minWidth: 0,
    width: '100%',
    overflow: 'hidden',
    display: 'block',
  },
  infoIcon: {
    height: 18,
    width: 18,
    color: theme.palette.icon.light,
    flexShrink: 0,
  },
  secondaryLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    color: theme.palette.text.secondary,
  },
  splitIcon: {
    height: 18,
    width: 18,
    color: theme.palette.text.secondary,
  },
  middleColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    overflow: 'hidden',
    minWidth: 0,
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-end',
    width: '100%',
    flexShrink: 0,
  },
  amountRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  manualChip: {
    height: 22,
    borderRadius: 12,
    backgroundColor: theme.palette.background.default,
  },
  amountFieldInput: {
    fontSize: 16,
    textAlign: 'right',
  },
  actionsColumn: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: '100%',
  },
}));

type Props = {
  txn: TransactionRecord;
  isIncome: boolean;
  baseId: string;
  onClick: (txn: TransactionRecord) => void;
};

const getCoaNode = (
  coaId: string,
  coaNodesById: OrderedMap<string, ChartOfAccountNodeProps>,
  value?: ChartOfAccountProps | null,
) => {
  let ret = coaNodesById ? coaNodesById.get(coaId) : null;

  if (chartOfAccountsUtils.isTransferCoaNotFound(value)) {
    ret = coaNodesById.get(chartOfAccountsUtils.TRANSFER_NOT_FOUND_COA_ID);
  }
  return ret;
};

export const SeriesHistoryRow = ({ txn, isIncome, baseId, onClick }: Props): JSX.Element => {
  const { classes } = useStyles();
  const accountsById = useAppSelector(accountsSelectors.getAccountsById);
  const coaNodesById = useAppSelector(chartOfAccountsSelectors.getChartOfAccountNodesById);

  const displayName = scheduledTransactionsUtils?.getDisplayName?.(txn) || txn.payee || '';
  const accountName = accountsById?.get(txn.accountId || '')?.name || 'Unknown';
  const txnDate = transactionsUtils.dateTimeFromTxn(txn);
  const dayString = txnDate.isValid ? txnDate.toLocaleString(DateTime.DATE_MED) : '—';

  const statementPayee = txn.cpData?.payee;
  // only show if it adds value beyond the main display name
  const showMoreInfo = Boolean(statementPayee && statementPayee.toLowerCase() !== displayName.toLowerCase());

  const isSplit = transactionsUtils.isSplitTxn(txn);
  const coaId = chartOfAccountsUtils.getCoaId(txn.coa);
  const categoryName = getCoaNode(coaId, coaNodesById, txn.coa)?.fullPathNameWithoutUsageType || 'Category';
  const isManual = txn.source === 'USER_ENTERED';
  const isManualMatch = txn.matchState === 'USER_MATCHED';

  const handleClick = useCallback(() => {
    onClick(txn);
  }, [onClick, txn]);

  return (
    <div
      id={`${baseId}-row`}
      role='button'
      tabIndex={0}
      aria-labelledby={`${baseId}-payee`}
      className={classes.root}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className={classes.leftColumn}>
        <div className={classes.payeeRow}>
          {showMoreInfo ? (
            <QTip title={`Original statement name: ${statementPayee}`} placement='top' arrow>
              <InfoIcon className={classes.infoIcon} />
            </QTip>
          ) : null}
          <OverflowTooltip title={displayName}>
            <Typography
              id={`${baseId}-payee`}
              variant='paragraphRegular'
              noWrap
              className={classes.payeeText}
              sx={{ color: 'text.secondary' }}
            >
              {displayName}
            </Typography>
          </OverflowTooltip>
        </div>

        {isSplit ? (
          <div className={classes.secondaryLine}>
            <CallSplitIcon className={classes.splitIcon} />
            <Typography variant='paragraphRegular' color='text.secondary'>
              Split
            </Typography>
          </div>
        ) : (
          <Typography variant='paragraphRegular' color='text.secondary' noWrap>
            {categoryName}
          </Typography>
        )}
      </div>

      <div className={classes.middleColumn}>
        <OverflowTooltip title={accountName}>
          <Typography id={`${baseId}-account`} variant='paragraphRegular' noWrap>
            {accountName}
          </Typography>
        </OverflowTooltip>
        <Typography id={`${baseId}-date`} variant='paragraphRegular' color='text.secondary' noWrap>
          on {dayString}
        </Typography>
      </div>

      <div className={classes.rightColumn}>
        <div className={classes.amountRow}>
          <QAmountField
            id={`${baseId}-amount`}
            value={Number(txn.amount)}
            editable={false}
            plain
            margin='none'
            textFieldVariant='filled'
            showSign='positive'
            showColor='positive'
            expectedSign={isIncome ? 'positive' : 'negative'}
            classes={{ input: classes.amountFieldInput }}
          />
        </div>

        {isManual ||
          (isManualMatch && (
            <Chip icon={<LinkIcon fontSize='small' />} label='Manual' size='small' className={classes.manualChip} />
          ))}
      </div>
      <div
        id='actions-column'
        role='presentation'
        className={classes.actionsColumn}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <TxnActionsMenuButton txn={txn} context='view-series-linked' />
      </div>
    </div>
  );
};
